//Library imports
import { Fragment, useState, ChangeEvent } from 'react';
import { useAsync } from 'react-async';
import { useHistory, useLocation } from 'react-router-dom';

//Services
import { getDivisionOptions } from "../../api/services/divisionService";

//Models and data types
import { selectOption } from '../../types/selectOption';

//Components
import Layout from '../../components/layout';
import SubmitButton from '../../components/submitButton';
import DataTable from '../../components/dataTable';
import SelectMulti from 'react-select';
import { graySelectStyles } from '../../common/selectStyles';
import { searchBillCreate, billExport } from '../../api/services/quickbooksService';
import QuickbooksBillRequest from '../../api/requests/quickbooksBillRequest';
import QuickbooksExportRequest from '../../api/requests/quickbooksExportRequest';
import Therapists from '../../api/models/therapists';
import { Notification } from '../../components/notifications';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import QuickBooksDateModal from '../../components/quickBooksDateModal';

//Get our view state
type ViewState = {
    message?: string;
};

/**
 * Methods to fetch search criteria with which to populate
 */
const fetchDivisions = async () => getDivisionOptions();
const doSearch = async (request: QuickbooksBillRequest) => await searchBillCreate(request);

/**
 * Primary Component
 */
const BillCreate = () => {
    //Process our DataTable columns and data
    const columns = () => [
        {
            Header: 'Therapist',
            accessor: (u: any) => {
                return (
                    <div className='grid text-left items-top'>
                        <div className='flex items-center'>
                            <input
                                key={u.userId}
                                type="checkbox"
                                className={"userChecked mr-3 primary-checkbox " + (u.showCheckbox ? 'visible' : 'invisible')}
                                id={u.userId}
                                value={u.userId}
                                onChange={(e) => handleClick(e, u.userId)}
                                checked={isCheck.includes(u.userId)}
                                disabled={!u.showCheckbox}
                            />
                            {u.name}
                        </div>
                    </div>
                );
            },
            className: 'align-top',
        },
        {
            Header: 'Status',
            accessor: (u: any) => {
                return u.status;
            },
            className: 'align-top',
            width: 50
        },
        {
            Header: 'Email',
            accessor: (u: any) => {
                return u.email;
            },
            className: 'align-top',
        },
        {
            Header: 'Paid Monthly',
            accessor: (u: any) => {
                return u.paymentSchedule === 'Monthly' ? 'Yes' : 'No';
            },
            className: 'align-top',
            width: 100
        },
        {
            Header: 'Division',
            accessor: (u: any) => {
                return u.division;
            },
            className: 'align-top',
        },
        {
            Header: 'Timesheets',
            accessor: (u: any) => {
                return (
                    <ul className='list-disc'>
                        {u.timesheetList.length === 0 && (
                            <li>No timesheet</li>
                        )}
                        {u.timesheetList.length > 0 && (
                            (u.timesheetList.map((g: any) => <li><b>{g.timesheetStatus} </b> - {g.districtName}</li>))
                        )}
                    </ul>
                );
            },
            className: 'align-top',
            width: 700,
        },
        {
            Header: 'Exports In Date Range',
            accessor: (u: any) => {
                return u.exportCount.toString();
            },
            className: 'text-center align-top',
        },
    ];

    //App hooks
    const history = useHistory();
    const { state } = useLocation<ViewState>();

    const doExport = async (request: QuickbooksExportRequest) => {
        setIsLoading(true);
        await billExport(request).finally(() => {
            history.push(`/accounting/bill/exports`)
        });
    }

    //Select element options
    const [divisionOptions, setdivisionOptions] = useState<selectOption[]>([{ value: -1, label: '' }]);

    //Data hooks
    const [fromDate, setFromDate] = useState<Date>();
    const [toDate, setToDate] = useState<Date>();
    const [timesheetStatus, setTimesheetStatus] = useState<number>(-1);
    const [paidMonthly, setPaidMonthly] = useState<number>(-1);
    const [divisionId, setDivisionId] = useState<number>(-1);
    const [defaultDivisionOption, setDefaultDivisionOption] = useState<selectOption>();
    const [searchData, setSearchData] = useState<Therapists[]>([]);
    const [fromDisplay, setfromDisplay] = useState<Date>();
    const [toDisplay, setToDisplay] = useState<Date>();
    const [isCheckAll, setIsCheckAll] = useState(false);
    const [isCheck, setIsCheck] = useState<Number[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showDateModal, setShowDateModal] = useState(false);

    const handleSelectAll = () => {
        const checkboxes = [...document.querySelectorAll('.userChecked:enabled')];
        if (checkboxes.length > 0)
            setIsCheckAll(!isCheckAll);
        setIsCheck(checkboxes.map(li => Number(li.id)));
        if (isCheckAll) {
            setIsCheck([]);
        }
    };

    const handleClick = (e: ChangeEvent<HTMLInputElement>, userId: number) => {
        const { checked } = e.target;
        setIsCheck([...isCheck, userId]);
        if (!checked) {
            setIsCheck(isCheck.filter(e => e !== userId));
        }
    };

    /**
    * Options lists (for our select elements)
    */
    const divisionAsync = useAsync({
        promiseFn: fetchDivisions,
        onResolve: (u) => {
            setdivisionOptions(u.data);

            if (divisionId > 0) {
                const dOption: selectOption = divisionOptions.filter((d) => d.value === divisionId)[0];
                setDefaultDivisionOption(dOption);
            }
        },
    });

    //Search button onClick handler
    const handleSearch = () => {
        setIsLoading(true);

        let toDateUtc = null;
        if (toDate) {
            var timezoneOffset = toDate.getTimezoneOffset();
            toDateUtc = moment(toDate).add(-timezoneOffset, 'm').toDate();
        }

        let fromDateUtc = null;
        if (fromDate) {
            var timezoneOffset = fromDate.getTimezoneOffset();
            fromDateUtc = moment(fromDate).add(-timezoneOffset, 'm').toDate();
        }

        const quickbooksBillRequest: QuickbooksBillRequest = {
            dateFrom: fromDateUtc!,
            dateTo: toDateUtc!,
            timesheetStatus: timesheetStatus,
            paidMonthly: paidMonthly,
            divisionId: divisionId
        }

        setfromDisplay(fromDate);
        setToDisplay(toDate);
        doSearch(quickbooksBillRequest).then(response => {
            setSearchData(response.data);
            setIsLoading(false);
        });
    };

    /*const handleExport = () => {
        const quickbooksExportRequest: QuickbooksExportRequest = {
            dateFrom: moment(fromDate?.toDateString()).toDate(),
            dateTo: moment(toDate?.toDateString()).toDate(),
            userIds: isCheck
        }
        doExport(quickbooksExportRequest);
    };*/

    const handleQuickBooksDateSelected = (
        transactionDate?: Date
    ) => {
        const quickbooksExportRequest: QuickbooksExportRequest = {
            dateFrom: moment(fromDate?.toDateString()).toDate(),
            dateTo: moment(toDate?.toDateString()).toDate(),
            userIds: isCheck,
            transactionDate: moment(transactionDate?.toDateString()).toDate()
        }
        setShowDateModal(false);
        doExport(quickbooksExportRequest);
    }

    const onChangePageSize = (pageSize: number) => {
        if (isCheckAll) {
            const checkboxes = [...document.querySelectorAll('.userChecked')];
            if (isCheckAll)
                setIsCheck(checkboxes.map(li => Number(li.id)));
        }
    }

    return (
        <Layout isLoading={
            divisionAsync.isPending ||
            isLoading
        }>
            <Fragment>
                {/* QuickBooks Transaction Date modal */}
                <QuickBooksDateModal
                    open={showDateModal}
                    onCancel={() => setShowDateModal(false)}
                    onApprove={(transactionDate?: Date) => handleQuickBooksDateSelected(transactionDate)}
                />
                <Fragment>
                    {state?.message && <Notification>{state?.message}</Notification>}

                    {/* Primary Container */}
                    <div className='z-0 px-4 py-5 bg-white sm:p-6'>
                        {/* Search Fields */}
                        <div className='z-0 grid grid-cols-6 gap-6'>
                            <div className='col-span-3 sm:col-span-3 md:col-span-2 lg:col-span-1'>
                                <label className='primary'>Date From <span className='text-red-500'>*</span></label>
                                <DatePicker
                                    name='date-from'
                                    selected={fromDate == null ? new Date() : fromDate}
                                    onChange={(date: Date) => setFromDate(date!)}
                                    dateFormat="MM/dd/yyyy"
                                    showFullMonthYearPicker
                                />
                            </div>
                            <div className='col-span-3 sm:col-span-3 md:col-span-2 lg:col-span-1'>
                                <label className='primary'>Date To <span className='text-red-500'>*</span></label>
                                <DatePicker
                                    name='date-to'
                                    selected={toDate == null ? new Date() : toDate}
                                    onChange={(date: Date) => setToDate(date!)}
                                    dateFormat="MM/dd/yyyy"
                                    showFullMonthYearPicker
                                />
                            </div>
                            <div className='col-span-6 sm:col-span-3 md:col-span-2 lg:col-span-1'>
                                <label className='primary'>Timesheet Status</label>
                                <SelectMulti
                                    className='block primary'
                                    isMulti={false}
                                    options={[
                                        { value: '-1', label: '-- Any --' },
                                        { value: '1', label: 'Approved' },
                                        { value: '0', label: 'Unapproved/Missing' }
                                    ]}
                                    value={[
                                        { value: '-1', label: '-- Any --' },
                                        { value: '1', label: 'Approved' },
                                        { value: '0', label: 'Unapproved/Missing' }
                                    ].find(o => o.value === String(timesheetStatus))}
                                    isSearchable={false}
                                    styles={graySelectStyles}
                                    onChange={(e: any) => setTimesheetStatus(Number(e.value))}
                                />
                            </div>
                            <div className='col-span-6 sm:col-span-3 md:col-span-2 lg:col-span-1'>
                                <label className='primary'>Paid Monthly</label>
                                <SelectMulti
                                    className='block primary'
                                    isMulti={false}
                                    options={[
                                        { value: '-1', label: '-- Any --' },
                                        { value: '1', label: 'Yes' },
                                        { value: '0', label: 'No' }
                                    ]}
                                    value={[
                                        { value: '-1', label: '-- Any --' },
                                        { value: '1', label: 'Yes' },
                                        { value: '0', label: 'No' }
                                    ].find(o => o.value === String(paidMonthly))}
                                    isSearchable={false}
                                    styles={graySelectStyles}
                                    onChange={(e: any) => setPaidMonthly(Number(e.value))}
                                />
                            </div>
                            <div className='col-span-6 sm:col-span-3 md:col-span-2 lg:col-span-1'>
                                <label className='primary'>Division</label>
                                <SelectMulti
                                    className='block primary'
                                    isMulti={false}
                                    options={divisionOptions}
                                    defaultValue={defaultDivisionOption}
                                    isClearable={true}
                                    placeholder='-- Any --'
                                    styles={graySelectStyles}
                                    onChange={(e: any | null) => {
                                        //Null exception handling
                                        e = (e !== null) ? e : { label: '', value: -1 };
                                        setDivisionId(Number(e.value))
                                    }}
                                />
                            </div>
                        </div>

                        {/* Button Row */}
                        <div className='grid grid-cols-2 gap-2 mt-6'>
                            <div className='col-span-1 sm:col-span-1 lg:col-span-1'>
                                <SubmitButton
                                    text='Search'
                                    className='mr-5 primary button'
                                    onClick={() => handleSearch()}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Results Table */}
                    {searchData !== null && searchData !== undefined && (
                        <div className='results-table'>
                            {searchData.length > 0 && (
                                <div className='overflow-hidden border-b border-gray-200 rounded-lg shadow'>
                                    <div className='flex mt-5 mb-5'>
                                        <div>
                                            <button
                                                type='button'
                                                className='px-4 py-2 ml-5 mr-3 font-semibold text-gray-800 bg-white border border-gray-400 hover:bg-gray-200'
                                                onClick={() => handleSelectAll()}
                                            >{isCheckAll ? 'Unselect All' : 'Select All'}</button>
                                        </div>
                                        <div>
                                            <button
                                                type='button'
                                                className='px-4 py-2 mr-3 font-semibold text-gray-800 bg-white border border-gray-400 hover:bg-gray-200'
                                                onClick={() => setShowDateModal(true)}
                                                disabled={isCheck.length < 1}
                                            >{'Create Export For ' + moment(fromDisplay).format('MMM D') + ' - ' + moment(toDisplay).format('MMM D')}</button>
                                        </div>
                                    </div>
                                    <DataTable
                                        columns={columns()}
                                        data={searchData}
                                        showPagination={searchData.length > 30 ? true : false}
                                        skipFilter={true}
                                        onChangePageSize={onChangePageSize}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </Fragment>
            </Fragment>
        </Layout>
    );
};

export default BillCreate;
